import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import './carousel.css';

export const wrapCarouselIndex = (index, count) => count ? ((index % count) + count) % count : 0;
const isField = target => !!target?.closest?.('input,textarea,select,[contenteditable="true"],[role="slider"],[role="combobox"],[data-carousel-no-drag]');
const watchDrag = (_, event) => !isField(event.target);

export const CarouselArrows = ({ id, previous, next }) => <div className="carousel-arrows">
  <Button type="button" variant="ghost" size="icon" data-testid={`${id}-previous`} aria-label="Previous card" title="Previous card" onClick={previous}><ChevronLeft aria-hidden="true" /></Button>
  <Button type="button" variant="ghost" size="icon" data-testid={`${id}-next`} aria-label="Next card" title="Next card" onClick={next}><ChevronRight aria-hidden="true" /></Button>
</div>;

export const LoopingDeck = ({ id, items, index, onIndexChange, renderItem, actionCards = false }) => {
  const [viewportRef, api] = useEmblaCarousel({ loop: true, align: 'center', containScroll: false, duration: 24,
    watchFocus: true, watchDrag });
  const [height, setHeight] = useState(null);
  const [dragging, setDragging] = useState(false);
  const contents = useRef(new Map());
  const selection = useRef({ index, onIndexChange });
  selection.current = { index, onIndexChange };
  const pointer = useRef(null);
  const reducedMotion = useReducedMotion();
  const signature = items.map(item => item.id).join('|');
  useEffect(() => {
    if (!api) return;
    api.scrollTo(wrapCarouselIndex(selection.current.index, items.length), true);
    const selected = () => {
      const next = api.selectedScrollSnap();
      if (next !== selection.current.index) selection.current.onIndexChange(next);
    };
    const down = () => setDragging(true), up = () => setDragging(false);
    api.on('select', selected).on('pointerDown', down).on('pointerUp', up);
    return () => { api.off('select', selected).off('pointerDown', down).off('pointerUp', up); };
  }, [api, signature, items.length]);
  useEffect(() => {
    if (api && api.selectedScrollSnap() !== index) api.scrollTo(wrapCarouselIndex(index, items.length));
  }, [api, index, items.length]);
  useLayoutEffect(() => {
    const node = contents.current.get(items[index]?.id);
    if (!node) return;
    const measure = () => setHeight(node.offsetHeight + 20);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [index, signature, items]);
  const keyboard = event => {
    if (event.defaultPrevented || isField(event.target)) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      onIndexChange(wrapCarouselIndex(index + (event.key === 'ArrowLeft' ? -1 : 1), items.length));
    }
  };
  return <motion.div className={`looping-deck ${actionCards ? 'looping-deck--actions' : ''}`}
    data-testid={id} animate={{ height: height || 'auto' }} transition={{ duration: reducedMotion ? 0 : .18 }}>
    <div ref={viewportRef} className="looping-viewport" data-testid={`${id}-viewport`} onKeyDown={keyboard}
      onScroll={event => { if (event.currentTarget.scrollLeft) event.currentTarget.scrollLeft = 0; }}
      onFocusCapture={() => { if (api) api.rootNode().scrollLeft = 0; }}
      onPointerDownCapture={event => { pointer.current = { x: event.clientX, y: event.clientY, moved: false, field: isField(event.target) }; }}
      onPointerMoveCapture={event => { const p = pointer.current; if (p && Math.hypot(event.clientX - p.x, event.clientY - p.y) > 10) p.moved = true; }}
      onClickCapture={event => { if (pointer.current?.moved && !pointer.current.field && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); } }}>
      <div className="looping-track">
        {items.map((item, position) => {
          const active = position === index;
          return <div key={item.id} className="looping-slide" role="group" aria-roledescription="slide"
            aria-label={item.title} aria-hidden={!active} inert={!active ? true : undefined} data-testid={`${id}-slide-${item.id}`}>
            <div className={`looping-slide-inner ${active ? 'is-active' : ''}`} ref={node => { if (node) contents.current.set(item.id, node); else contents.current.delete(item.id); }}>
              <motion.div animate={{ y: actionCards && active && !dragging && !reducedMotion ? [0, -3, 0] : 0 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
                {renderItem(item, active)}
              </motion.div>
            </div>
          </div>;
        })}
      </div>
    </div>
  </motion.div>;
};